import React, { useState } from 'react';
import {
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { resetPassword, sendVerificationCode } from '../../core/services/authService';
import {
  BASE_URL, TENANT_DOMAIN,
} from '../../config';
import logo from '../../assets/images/ryffine_big_logo.png';

const useStyles = makeStyles(theme => ({
  outerContainer: {
    padding: theme.spacing(5),
  },
  root: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  header: {
    backgroundColor: '#1C355E',
    padding: theme.spacing(2.5),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    maxWidth: '200px',
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
  },
  content: {
    padding: theme.spacing(4),
  },
  link: {
    textDecoration: 'none',
    fontSize: '14px',
    color: '#007bff',
  },
}));

const ForgotPassword = () => {
  const classes = useStyles();
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const history = useHistory();

  const handleSendCode = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await sendVerificationCode(BASE_URL, TENANT_DOMAIN, username);
      if (result.success) {
        setMessage(result.message);
        setStep(2);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      const result = await resetPassword(
        BASE_URL,
        TENANT_DOMAIN,
        username,
        verificationCode,
        newPassword,
      );
      if (result.success) {
        setMessage(result.message);
        history.push('/login');
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid
      container
      className={`df-center ${classes.outerContainer}`}
    >
      <Grid item xs={10} sm={6} md={4} className={classes.root}>
        <Grid container direction="column">
          <Grid item xs={12} className={classes.header}>
            <img src={logo} alt="Logo" className={classes.logo} />
          </Grid>
          <Grid item xs={12} className={classes.content}>
            <Grid container direction="column" spacing={3}>
              <Grid item className="df-center">
                <Typography variant="h5" align="center">
                  {step === 1 ? 'Forgot Password' : 'Reset Password'}
                </Typography>
              </Grid>
              {error && (
                <Grid item>
                  <Typography color="error" align="center">
                    {error}
                  </Typography>
                </Grid>
              )}
              {message && (
                <Grid item>
                  <Typography color="primary" align="center">
                    {message}
                  </Typography>
                </Grid>
              )}
              {step === 1 && (
                <>
                  <Grid item>
                    <TextField
                      label="Username"
                      variant="outlined"
                      fullWidth
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item className="df-center">
                    <Button
                      className="button"
                      style={{ minWidth: '110px' }}
                      variant="contained"
                      color="primary"
                      onClick={handleSendCode}
                      disabled={!username || loading}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Send Code'
                      )}
                    </Button>
                  </Grid>
                </>
              )}
              {step === 2 && (
                <>
                  <Grid item>
                    <TextField
                      label="Verification Code"
                      variant="outlined"
                      fullWidth
                      value={verificationCode}
                      onChange={e => setVerificationCode(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      label="New Password"
                      variant="outlined"
                      type="password"
                      fullWidth
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      onChange={e => setConfirmPassword(e.target.value)}
                      error={newPassword !== confirmPassword}
                      label="Confirm Password"
                      value={confirmPassword}
                      variant="outlined"
                      type="password"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item className="df-center">
                    <Button
                      disabled={
                        !verificationCode
                        || !newPassword
                        || !confirmPassword
                        || newPassword !== confirmPassword
                        || loading
                      }
                      style={{ minWidth: '160px' }}
                      onClick={handleResetPassword}
                      variant="contained"
                      className="button"
                      color="primary"
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Reset Password'
                      )}
                    </Button>
                  </Grid>
                </>
              )}
              <Grid item className="df-center">
                <Typography variant="body2">
                  <Link href="/login" className={classes.link}>
                    Back to Login
                  </Link>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;
